/**
* Common ExternalBrandContent component used by other modules as a reusable component which returns the brand content from external partners in product page.
*
* @module views/Molecules/ExternalBrandContent
* @memberof -Common
*/
import './ExternalBrandContent.scss';

import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import { constants } from '@ulta/utils/constants/constants';

import JSscriptTag from '../JSscriptTag/JSscriptTag';
import * as utils from './ExternalBrandContent';


/**
* Represents a ExternalBrandContent component
*
* @method
* @param {ExternalBrandContentProps} props - React properties passed from composition
* @returns ExternalBrandContent Content
*/
const ExternalBrandContent = ( props ) => {
  const { breakpoint } = useDeviceInflection();
  const { salsifyClientId } = useAppConfigContext();

  const {
    showMoreLabel,
    showLessLabel,
    siteId,
    productId,
    rootMargin = defaultProps.rootMargin,
    root = defaultProps.root,
    threshold = defaultProps.threshold,
    id
  } = props;
  const [hasSyndigoContent, setHasSyndigoContent] = useState( false );
  const [hasContentLoaded, setContentLoaded] = useState( global.hasContentLoaded );
  const [showMoreContent, setShowMoreContent] = useState( false );
  const ref = useRef();
  const [headerHeight, setheaderHeight] = useState();
  const handleOnClick = () => {
    setShowMoreContent( !showMoreContent );
    if( showMoreContent ){
      ref.current?.scrollIntoView( true );
      let scrolledY = window.scrollY;
      if( scrolledY && headerHeight ){
        window.scroll( 0, scrolledY - headerHeight );
      }
    }
  };

  const [currentExternalBrandContentRef, setCurrentExternalBrandContentRef] = useState( '' );
  const isIntersecting = useIntersectionObserver( ref, {
    root: root,
    rootMargin: rootMargin,
    threshold: threshold
  }, handleIntersection( props ) );

  useEffect( () => {
    if( siteId && productId ){
      utils.checkSyndigoAvailability( { siteId, productId }, { setHasSyndigoContent } );
    }
    return () => {
      setHasSyndigoContent( false );
      setContentLoaded( false );
    };
  }, [] );
  useEffect( () => {
    // set ref
    if( ref.current ){
      setCurrentExternalBrandContentRef( ref.current );
    }
  }, [hasContentLoaded, ref.current] );

  useEffect( () => {
    const isDesktop = breakpoint && ( breakpoint.CURRENT_BREAKPOINT === 'MD' || breakpoint.CURRENT_BREAKPOINT === 'LG' || breakpoint.CURRENT_BREAKPOINT === 'XL' || breakpoint.CURRENT_BREAKPOINT === 'SM' );
    const headerClass = isDesktop ? 'DesktopHeader__StickyHeader' : 'navigation__wrapper--sticky';
    const headerElement = document.getElementsByClassName( headerClass );
    if( headerElement ){
      setheaderHeight( headerElement?.[0]?.clientHeight );
    }
    if( !global.hasContentLoaded ){
      const checkSyndigoLoaded = setInterval( () => {
        if( global.hasContentLoaded ){
          clearInterval( checkSyndigoLoaded );
          setContentLoaded( true );
        }
      }, 100 );
    }
    return ()=>{
      global.hasContentLoaded = false;
      setContentLoaded( false );
    };

  }, [breakpoint.CURRENT_BREAKPOINT] );

  const ExternalBrandContent = hasSyndigoContent ? (
    <SyndigoContent
      productId={ productId }
      siteId={ siteId }
    />
  ) : (
    <SalisifyContent
      productId={ productId }
      clientId={ salsifyClientId }
    />
  );

  return (
    <>
      <ShowMoreContainer
        showMoreContent={ showMoreContent }
        showLessLabel={ showLessLabel }
        showMoreLabel={ showMoreLabel }
        handleOnClick={ handleOnClick }
        hasContentLoaded={ hasContentLoaded }
        refId={ ref }
        id={ id }
      >
        { ExternalBrandContent }
      </ShowMoreContainer>


    </>
  );
};

/**
 * SyndigoContent
 *
 * A component that renders Syndigo's content.
 *
 * @param {string} productId - The ID of the product for which content is being fetched.
 * @param {string} siteId - The ID of the site for syndigo.
 *
 */
export const SyndigoContent = ( props ) => {
  const { productId, siteId, componentKey } = props;
  return (
    <>
      <JSscriptTag
        tagId='SyndigoId'
        body={ `(function (s, y, n, di, go) {
        di = s.createElement(y);
        di.type = 'text/java'+y;
        di.async = true;
        di.src = n + Math.floor(${componentKey} / 86400000);
        go = s.getElementsByTagName(y)[0];
        go.parentNode.insertBefore(di,go);
      }(document,'script', 'https://content.syndigo.com/site/${siteId}/tag.js?cv='));` }
        divId={ 'syndi_inline' }
      />
      <JSscriptTag
        tagId='SyndigoScriptId'
        body={ `window.SYNDI = window.SYNDI || [];
      SYNDI.push('${productId}');
      window.SYNDI.push({
      'contentCallback': function (hasContent) {
        if (hasContent) {
          window.hasContentLoaded = true;
        }
      }
    });` }
        defer={ true }
      />
    </>
  );
};


/**
 * SalisifyContent
 *
 * A component that renders Salsify's content.
 *
 * @param {string} productId - The ID of the product for which content is being fetched.
 * @param {string} clientId - The ID of salsify client.
 *
 */
export const SalisifyContent = ( props ) => {
  const { productId, clientId } = props;
  return (
    <>
      <JSscriptTag
        tagId='salsifyScriptId'
        src={ 'https://salsify-ecdn.com/sdk/latest/salsify.js' }
        defer={ true }
        divId={ 'enhanced-content-container' }
      />
      <JSscriptTag
        tagId='salsifySdkId'
        body={
          `window.salsifyExperiencesSdkLoaded = async function(salsify) {
            const clientId = '${clientId}';
            salsify.init({
              clientId,
              enhancedContent: {
                 idType: "Ulta Product ID"
              }
            });

            const productId = '${productId}';

            const exists = await salsify.enhancedContent.exists(productId, "Ulta Product ID");
            if (exists) {
              salsify.enhancedContent.renderIframe(
                document.querySelector('#enhanced-content-container'),
                productId
              );
              window.hasContentLoaded = true;
            }
          }`
        }
      />
    </>
  );
};

/**
 * checkSyndigoAvailability
 *
 * An async function that checks the availability of Syndigo content for a given productId.
 *
 * @param {string} siteId - The ID of the site.
 * @param {string} productId - The ID of the product.
 * @param {function} setHasSyndigoContent - Method to update the content availability status.
 *
 */
export const checkSyndigoAvailability = async( data, methods ) => {
  const { siteId, productId } = data;
  if( !siteId || !productId ){
    return;
  }
  const { setHasSyndigoContent } = methods;
  const url = `https://content.syndigo.com/page/${siteId}/${productId}.json`;

  const response = await fetch( url );
  const jsonData = await response.json();
  if( response.ok && jsonData && jsonData.experience && ( Object.keys( jsonData.experience?.experiences )?.length > 0 ) ){
    setHasSyndigoContent && setHasSyndigoContent( true );
  }
};

/**
 * ShowMoreContainer
 *
 * A component that wraps external brand content with in container with show more or less buttons.
 *
 * @param {Object} ref - A reference to the container element.
 * @param {string} id - The ID for the container element.
 * @param {boolean} showMoreContent - Flag indicating whether to show more content.
 * @param {Object} showLessLabel - Label for the "Show Less" button.
 * @param {Object} showMoreLabel - Label for the "Show More" button.
 * @param {function} handleOnClick - Click handler for the show more/less button.
 * @param {boolean} hasContentLoaded - Flag indicating whether content has loaded.
 *
 */
export const ShowMoreContainer = ( props ) => {
  const { children } = props;
  const {
    refId,
    id,
    showMoreContent,
    showLessLabel,
    showMoreLabel,
    handleOnClick,
    hasContentLoaded } = props;
  return (
    <div ref={ refId }
      id={ id }
      className='ExternalBrandContent'
    >
      <div className='ExternalBrandContent__wrapper'>
        <div className={ classNames( 'ExternalBrandContent__content', { 'ExternalBrandContent__content--showMore': showMoreContent, 'ExternalBrandContent__content--showLess': ( !showMoreContent && hasContentLoaded ) } ) }>
          { hasContentLoaded && ( <div className={ classNames( 'ExternalBrandContent__content--overlay', { 'ExternalBrandContent__content--overlayHidden': showMoreContent } ) }></div> ) }
          { children }
        </div>
        { hasContentLoaded && (
          <div className='ExternalBrandContent__expander'>
            <Button label={ showMoreContent ? showLessLabel?.label : showMoreLabel?.label }
              onClick={ handleOnClick }
              action={ showMoreContent ? showLessLabel : showMoreLabel }
              secondary
            />
          </div> ) }
      </div>

    </div>
  );
};

/**
* Property type definitions
* @typedef ExternalBrandContentProps
* @type {object}
* @property {string} siteId - Sets the siteId.
* @property {object} showMoreLabel - Sets the label for show more button.
* @property {object} showLessLabel - Sets the label for show less button.
* @property {string} productId - Sets the product ID.
* @property {string} id - Sets the value for id.
*/
export const propTypes = {
  /**
  * The prop that holds the siteId.
  */
  siteId: PropTypes.string.isRequired,
  /**
  * The prop that holds the show less button label and graphql.
  */
  showLessLabel: PropTypes.shape( {
    label: PropTypes.string,
    dataCaptureData: PropTypes.object
  } ),
  /**
  * The prop that holds the show more button label.
  */
  showMoreLabel: PropTypes.shape( {
    label: PropTypes.string,
    dataCaptureData: PropTypes.object
  } ),
  /**
  * The prop that decides to enable asking question or not.
  */
  productId: PropTypes.string,
  /**
  * The prop holds the id value.
  */
  id:PropTypes.string
};

export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

ExternalBrandContent.propTypes = propTypes;
ExternalBrandContent.defaultProps = defaultProps;

export default ExternalBrandContent;